import { forwardRef, useId } from 'react';
import { Underline } from '../Underline';
import TsrTooltip from '../../tooltip';
import TsrSvgIcon from '../../assets/icons';
import { Label } from '../Label';
import { MaskName } from '../models/MaskName';
import { InputPropsBase } from '../interfaces/Inputs';
import { TextType } from '../models/TextType';
import { InputContainer } from '../InputContainer';
import './styles.scss';
import { TooltipType } from '../models/TooltipType';
import { Variant } from '../models';
import React from 'react';

export interface TextProps extends InputPropsBase<string, string> {
    mask?: MaskName;
    type?: TextType;
    password?: boolean;
    autofocus?: boolean;
    dateHasBeenChangedAlgorithmically?: boolean;
    setDateHasBeenChangedAlgorithmically?: (changed: boolean) => void;
    children?: React.ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextProps>((props, ref) => {
    const id = useId();

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        props.onChange?.(value);

        // If algorithmic date changes are used
        props.setDateHasBeenChangedAlgorithmically?.(false);
    };

    const onBlur = () => {
        // Some variants want blur to emit value
        if (props.variant === Variant.AgGrid || props.variant === Variant.SearchBar) {
            props.onBlur?.(props.value ?? '');
            return;
        }

        // Other variants treat blur like submit
        props.onChange?.(props.value ?? '');
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (target.value === '') {
            props.onChange?.('');
        }
    };

    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            className={`${props.type === TextType.SearchBox ? 'searchbox' : ''} ${props.className || ''} ${props.variant}`}
            width={props.width}
            height={props.height}
            overflow={props.overflow}
            inputContainerLength={props.inputContainerLength}
        >
            <Label
                id={props.id || id}
                content={props.label}
                disabled={props.disabled === true}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength}
                className={props.labelClassName}
            >
                <div
                    className={'tsr-input-text' + (props.className ? ' ' + props.className : '')}
                    style={{ flex: props.inputLength || 1 }}
                >
                    <input
                        ref={ref}
                        id={props.id}
                        disabled={props.disabled === 'inputOnly' || props.disabled === true}
                        placeholder={props.placeholder}
                        value={props.value ?? ''}
                        onChange={onChange}
                        onBlur={onBlur}
                        onKeyUp={onKeyUp}
                        onClick={props.onClick}
                        tabIndex={props.tabIndex}
                        type={props.password ? 'password' : 'text'}
                        autoFocus={props.autofocus}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        className={props.disabled === 'inputOnly' || props.disabled === true ? 'disabled' : ''}
                    />

                    {props.children}

                    {props.errorMessage && !props.disabled ? (
                        <TsrTooltip type={TooltipType.Error} icon content={props.errorMessage}>
                            <TsrSvgIcon icon="information" color="error" pointer />
                        </TsrTooltip>
                    ) : props.infoMessage && !props.disabled ? (
                        <TsrTooltip icon content={props.infoMessage}>
                            <TsrSvgIcon icon="information" color="info" pointer />
                        </TsrTooltip>
                    ) : (
                        props.infoMessage && <TsrSvgIcon icon="information" color="disabled" />
                    )}

                    <Underline disabled={props.disabled === 'inputOnly' || props.disabled === true} />
                </div>
            </Label>
        </InputContainer>
    );
});
